import { ReactNode, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useScrollMode, bookPageClass } from './ScrollMode';

interface StackSectionProps {
  id?: string;
  /** navy = dark glass panel · light = solid light panel · glass = transparent frosted blur */
  theme?: 'navy' | 'light' | 'glass';
  sticky?: boolean;
  parallax?: number;
  className?: string;
  children: ReactNode;
  imageSrc?: string;
}

export function StackSection({
  id,
  theme = 'navy',
  sticky,
  parallax = 40,
  className = '',
  children,
  imageSrc,
}: StackSectionProps) {
  const { mode } = useScrollMode();
  const ref = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [parallax, -parallax]);

  // Three distinct surface styles
  let surface: string;
  if (imageSrc) {
    surface = 'border-white/10 text-white'; // transparent — image shows through
  } else if (theme === 'light') {
    surface = 'bg-slate-50 border-slate-200/70 text-slate-900 ticca-light-section';
  } else if (theme === 'glass') {
    surface = 'bg-[#04081e]/90 backdrop-blur-xl border-white/10 text-white';
  } else {
    // navy — dark glass
    surface = 'bg-slate-900/85 backdrop-blur-xl border-white/10 text-white';
  }

  // Overlay opacity for image sections
  const overlayColor = 'rgba(4, 8, 24, 0.52)';

  const isStack = sticky ?? mode === 'stack';
  const isBook = mode === 'book';
  const useParallax = parallax > 0 && !isBook && !isStack;
  const paneHeight = isStack ? 'min-h-[calc(100vh-80px)]' : '';

  return (
    <section
      id={id}
      style={{ position: isStack ? 'sticky' : 'relative', top: isStack ? 80 : 0 }}
      className={`border-t rounded-t-3xl shadow-[0_-32px_80px_-20px_rgba(0,0,0,0.45)] overflow-hidden ${surface} ${bookPageClass(mode)} ${paneHeight} ${className}`}
    >
      {imageSrc && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${imageSrc})` }}
          />
          <div className="absolute inset-0" style={{ background: overlayColor }} />
        </>
      )}
      <div className="relative z-10 mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center px-6 py-12 md:px-10 md:py-16">
        <div ref={ref}>
          <motion.div style={{ y: useParallax ? y : undefined }}>{children}</motion.div>
        </div>
      </div>
    </section>
  );
}
